"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { Table, TextBody } from "@opensea/ui-kit";
import { useItemsWithSkeletons } from "@opensea/ui-kit/hooks";
import { range } from "es-toolkit";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { ActivityFeedLiveTimestampFragment } from "@/components/activity-feed/ActivityFeedLiveTimestamp.fragment";
import { ActivityPriceFragment } from "@/components/activity-feed/ActivityPrice.fragment";
import { ACTIVITY_FEED_PAGE_SIZE } from "@/components/activity-feed/constants";
import { DropStatus } from "@/components/collection/drops/constants";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { useCollectionActivity } from "@/hooks/api/activity/use-collection-activity";
import { useDropStore } from "../CollectionDropContext";
import { MintFeedTableHeader } from "./MintFeedTableHeader";
import { MintFeedTableRow } from "./MintFeedTableRow";
import { MintFeedTableRowFragment } from "./MintFeedTableRow.fragment";

/**
 * Combines mint items by transaction hash.
 * This is necessary because the mint feed returns one mint activity per NFT.
 * We want to display one row for the user with quantities and prices combined per transaction.
 */
const combineMintItems = items => {
  const combinedItems = new Map();
  items.forEach(item => {
    const activity = readFragment(MintFeedTableRowFragment, item);
    // activity can be undefined and break table rendering, so check for that

    if (activity?.__typename !== "Mint") {
      return;
    }
    const activityLiveTimestamp = readFragment(ActivityFeedLiveTimestampFragment, activity);
    const price = readFragment(ActivityPriceFragment, activity);
    const tokenPrice = readFragment(TokenPriceFragment, price.price);
    const key = activityLiveTimestamp.transactionHash;
    if (!key) {
      return;
    }
    const existing = combinedItems.get(key);
    const quantity = Number.parseInt(activity.quantity, 10) || 1;
    if (existing) {
      existing.quantity += quantity;
      existing.price.token.unit += tokenPrice?.token.unit ?? 0;
      existing.price.usd += tokenPrice?.usd ?? 0;
    } else {
      // @ts-expect-error fragment types are getting confused here
      combinedItems.set(key, {
        ...activity,
        quantity,
        price: {
          token: {
            ...tokenPrice?.token,
            unit: tokenPrice?.token.unit ?? 0
          },
          usd: tokenPrice?.usd ?? 0
        }
      });
    }
  });
  return Array.from(combinedItems.values());
};
export const MintFeedTable = withSuspense(() => {
  const [status, collectionSlug] = useDropStore(useShallow(state => [state.status, state.collectionSlug]));
  const t = useTranslations("MintModule");
  const filter = {
    activityTypes: ["MINT"]
  };
  const {
    items: paginatedItems,
    pagination,
    itemsLiveAddedAt,
    noResults
  } = useCollectionActivity({
    collectionSlug,
    limit: ACTIVITY_FEED_PAGE_SIZE,
    filter
  });
  const combinedItems = combineMintItems(paginatedItems.flatMap(v => v ? v : []));
  const items = useItemsWithSkeletons({
    items: combinedItems,
    pageSize: ACTIVITY_FEED_PAGE_SIZE,
    isLoadingNext: pagination.isLoadingNext
  });
  const rowContext = {
    itemsLiveAddedAt
  };
  if (noResults) {
    return <div className="relative h-full overflow-hidden">
          <TextBody className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-[1]">
            {status === DropStatus.MINTING_SOON ? t("notStartedYet") : t("noMintsYet")}
          </TextBody>
          <MintFeedTableSkeleton context={{
        ...rowContext,
        override: {
          Skeleton: {
            className: "animate-none"
          }
        }
      }} />
        </div>;
  }
  return <Table context={rowContext} header={<MintFeedTableHeader />} itemKey={v => readFragment(MintFeedTableRowFragment, v)?.id} items={items} renderRow={MintFeedTableRow} size="md" {...pagination} />;
}, {
  //TODO: skeleton
  fallback: <MintFeedTableSkeleton />
});
export function MintFeedTableSkeleton(t0) {
  const $ = _c(4);
  const {
    context
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <MintFeedTableHeader />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = range(20).map(_temp2);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== context) {
    t3 = <Table context={context} header={t1} itemKey={_temp} items={t2} renderRow={MintFeedTableRow} size="md" />;
    $[2] = context;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp2() {}
function _temp(_, index) {
  return index;
}